/**
 * Task 28: Forgot Password Page
 * 3-step wizard: Email → OTP → New Password
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Key, Lock, ArrowLeft, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axiosInstance from '../config/axiosConfig';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength validation
  const validatePasswordStrength = (password) => {
    const requirements = [
      { test: password.length >= 8, text: 'At least 8 characters' },
      { test: /[A-Z]/.test(password), text: 'One uppercase letter' },
      { test: /[a-z]/.test(password), text: 'One lowercase letter' },
      { test: /\d/.test(password), text: 'One digit' },
      { test: /[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(password), text: 'One special character' }
    ];
    return requirements;
  };

  const passwordRequirements = validatePasswordStrength(formData.newPassword);
  const passwordIsValid = passwordRequirements.every(req => req.test);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axiosInstance.post('/auth/request-otp', { email: formData.email });
      setSuccess('OTP sent to your email! Please check your inbox.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axiosInstance.post('/auth/verify-otp', {
        email: formData.email,
        otp: formData.otp
      });
      setSuccess('OTP verified successfully!');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordIsValid) {
      setError('Password does not meet security requirements');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axiosInstance.post('/auth/reset-password', {
        email: formData.email,
        otp: formData.otp,
        new_password: formData.newPassword
      });
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy-dark to-teal flex items-center justify-center p-6">
      <div className="bg-navy-dark border border-teal/30 rounded-xl shadow-2xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gold mb-2">Reset Password</h1>
          <p className="text-textLight/70">Step {step} of 3</p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-between mb-8">
          {[1, 2, 3].map((stepNum) => (
            <div key={stepNum} className="flex-1 flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                stepNum < step ? 'bg-teal text-white' :
                stepNum === step ? 'bg-gold text-navy' :
                'bg-navy-light text-textLight/50'
              }`}>
                {stepNum < step ? '✓' : stepNum}
              </div>
              {stepNum < 3 && (
                <div className={`flex-1 h-1 mx-2 transition-colors ${
                  stepNum < step ? 'bg-teal' : 'bg-navy-light'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-teal/10 border border-teal/50 rounded-lg flex items-center gap-2">
            <CheckCircle className="text-teal flex-shrink-0" size={20} />
            <p className="text-teal text-sm">{success}</p>
          </div>
        )}

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleRequestOTP}>
            <div className="mb-6">
              <label className="block text-textLight mb-2 font-medium">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-textLight/50" size={20} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-navy border border-teal/30 rounded-lg pl-10 pr-4 py-3 text-textLight focus:border-teal focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  required
                  autoFocus
                />
              </div>
              <p className="text-textLight/50 text-xs mt-2">
                We'll send a 6-digit OTP to this email address
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal hover:bg-teal/80 disabled:bg-teal/50 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOTP}>
            <div className="mb-6">
              <label className="block text-textLight mb-2 font-medium">Enter OTP</label>
              <div className="relative">
                <Key className="absolute left-3 top-3 text-textLight/50" size={20} />
                <input
                  type="text"
                  value={formData.otp}
                  onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  className="w-full bg-navy border border-teal/30 rounded-lg pl-10 pr-4 py-3 text-textLight text-center text-2xl tracking-widest font-mono focus:border-teal focus:outline-none transition-colors"
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
              <p className="text-textLight/50 text-xs mt-2">
                Check your email for the 6-digit code (expires in 10 minutes)
              </p>
            </div>
            <button
              type="submit"
              disabled={loading || formData.otp.length !== 6}
              className="w-full bg-teal hover:bg-teal/80 disabled:bg-teal/50 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full mt-3 text-textLight/70 hover:text-teal text-sm transition-colors"
            >
              Resend OTP
            </button>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword}>
            <div className="mb-4">
              <label className="block text-textLight mb-2 font-medium">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-textLight/50" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  className="w-full bg-navy border border-teal/30 rounded-lg pl-10 pr-12 py-3 text-textLight focus:border-teal focus:outline-none transition-colors"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-textLight/50 hover:text-textLight transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-textLight mb-2 font-medium">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-textLight/50" size={20} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full bg-navy border border-teal/30 rounded-lg pl-10 pr-12 py-3 text-textLight focus:border-teal focus:outline-none transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3 text-textLight/50 hover:text-textLight transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="mb-6 p-4 bg-navy rounded-lg border border-teal/20">
              <p className="text-textLight/70 text-sm font-medium mb-2">Password Requirements:</p>
              <ul className="space-y-1">
                {passwordRequirements.map((req, index) => (
                  <li key={index} className={`text-xs flex items-center gap-2 ${
                    req.test ? 'text-teal' : 'text-textLight/50'
                  }`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${
                      req.test ? 'bg-teal/20' : 'bg-navy-light'
                    }`}>
                      {req.test ? '✓' : '○'}
                    </span>
                    {req.text}
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading || !passwordIsValid || formData.newPassword !== formData.confirmPassword}
              className="w-full bg-teal hover:bg-teal/80 disabled:bg-teal/50 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        )}

        {/* Back to Login */}
        <button
          onClick={() => navigate('/login')}
          className="w-full mt-6 text-textLight/70 hover:text-teal flex items-center justify-center gap-2 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Login
        </button>
      </div>
    </div>
  );
}
